import { ChangeDetectionStrategy, Component, Input, ChangeDetectorRef, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ShipmentTemplateService } from '@features/shipment-creation-template/services/shipment-template.service';
import { ShipmentCreationTemplate } from '@features/shipments/models/shipment-template.model';
import { UserApi } from '@shared-features/services';
import { ShipmentCreationTemplateTypes } from '@shared-features/enums/shipments.enum';
import { dedupeTemplatesByType } from '@features/shipments/utils/shipment-template.util';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { isBulkShipmentsVisible, isYccCustomer } from '@shared-features/utils/bulk-shipments-eligibility.util';
import { Observable } from 'rxjs';
import { map, filter, tap } from 'rxjs/operators';
import { UserRole } from '@shared/enums/global.enum';
import { PreCreateShipmentDialogComponent } from '@layout/components/sidenav/pre-create-shipment-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { UserRoleService } from '@shared/services/user-role.service';

@Component({
  selector: 'app-add-shipment-button',
  templateUrl: './add-shipment-button.component.html',
  styleUrls: ['./add-shipment-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddShipmentButtonComponent implements OnInit {
  @Input() color: 'primary' | 'accent' | 'warn' = 'primary';
  ShipmentTemplateTypes = ShipmentCreationTemplateTypes;
  showTemplatesMenu = false;
  availableTemplates$: Observable<ShipmentCreationTemplate[]>;
  defaultTemplates: ShipmentCreationTemplate;
  templates: ShipmentCreationTemplate[];

  /**
   * Bulk shipment creation (FR-001/FR-002). The two conditions are deliberately separate:
   * a YCC customer sees the entry DISABLED so the capability stays discoverable, while a
   * customer without the basic template does not see it at all. Both are blocked at the route
   * by BulkShipmentsGuard — this is presentation only.
   */
  showBulkShipments = false;
  isBulkShipmentsDisabled = false;

  constructor(
    private shipmentTemplateService: ShipmentTemplateService,
    private router: Router,
    private userApi: UserApi,
    private changeRef: ChangeDetectorRef,
    private matDialog: MatDialog,
    private userRoleService: UserRoleService
  ) {}

  ngOnInit(): void {
    this.availableTemplates$ = this.userApi.currentUser$.asObservable().pipe(
      filter((user) => !!user),
      map((user) => user.creationTemplates || []),
      tap((templates: ShipmentCreationTemplate[]) => {
        templates?.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
        this.changeRef.detectChanges();
        this.changeRef.markForCheck();
        this.showTemplatesMenu = templates?.length >= 1;
        return templates;
      })
    );
    this.availableTemplates$.subscribe((data) => {
      this.templates = dedupeTemplatesByType(data);
      this.defaultTemplates = this.templates.find((temp) => temp.isDefault);

      const currentUser = this.userApi.currentUser$.value;
      this.showBulkShipments = isBulkShipmentsVisible(currentUser);
      this.isBulkShipmentsDisabled = isYccCustomer(currentUser);

      // Show the dropdown when there is a real choice: more than one template, or bulk is
      // available alongside the single default (a lone template stays a plain button).
      this.showTemplatesMenu = this.templates?.length > 1 || this.showBulkShipments;
      this.changeRef.markForCheck();
    });
  }

  get isOrganizationOwner(): boolean {
    return this.userRoleService.isUserHasRoles([UserRole.OrganizationOwner]);
  }

  get hasTemplateMenuItems(): boolean {
    return this.templates?.length > 0 || this.showBulkShipments;
  }

  onClickCreateShipment(): void {
    this.matDialog.open(PreCreateShipmentDialogComponent);
  }

  navigateToTemplate(template: ShipmentCreationTemplateTypes): void {
    if (this.isOrganizationOwner) {
      this.matDialog.open(PreCreateShipmentDialogComponent);
    }
    else{
      this.router.navigateByUrl(this.shipmentTemplateService.getCreateUrl(template));
    }
  }

  navigateToBulkShipments(): void {
    this.router.navigateByUrl(RoutesUtil.BulkShipments.url());
  }

  navigateToBulkImport(): void {
    this.router.navigateByUrl(RoutesUtil.ShipmentTemplateBulkImport.url());
  }
}
